/**
 * Public VirtualSpace share/explore viewport layout — `VirtualSpacePublicThemeShell`
 * plus the `<main>` chrome under `/share/[token]` (virtual_space branch).
 *
 * ## Architecture (2026-05-07)
 *
 * The page now uses **document-level (body) scroll**, not an inner
 * `overflow-y-auto` band. The header is pinned via `position: sticky` so it
 * stays visible during scroll; the footer sits at the natural document
 * bottom; everything in between flows normally inside `<main>`.
 *
 * ### Why the architecture flipped
 *
 * Pre-2026-05-07 we used an "app-shell" pattern: shell capped at `100dvh`
 * with `overflow-hidden`, header + footer `shrink-0`, middle band
 * `flex-1 overflow-y-auto`. That kept the chrome pinned without `sticky`
 * and prevented body bounce on iOS, but it broke iOS Safari's standard
 * **tap-status-bar-to-scroll-to-top** behaviour (iOS only honours that
 * gesture for the topmost window/body scroller; an inner scroll
 * container is invisible to it). Founder asked for that gesture back.
 *
 * The flip also fixes:
 *   - Browser back/forward scroll restoration falling back to native
 *   - Pull-to-refresh respecting natural body bounds
 *   - `scrollIntoView` from anchor links targeting the right scroller
 *   - The "scroll past a viewer that captures touch" trap (combined
 *     with `touch-action: pan-y` on each viewer wrapper)
 *
 * ### Edge cases the new layout still satisfies
 *
 * 1. **Header pinning** — `position: sticky top-0 z-30` on the `<header>`
 *    keeps the chrome visible during scroll without re-introducing the
 *    `flex` shell.
 * 2. **iOS dynamic viewport** — `min-h-dvh` on the shell so the
 *    document is at least as tall as the dynamic viewport (avoids a
 *    short-content page leaving a blank gap above the footer).
 * 3. **Print / PDF** — Default body flow already prints right.
 * 4. **`prefers-reduced-motion`** — `motion-reduce:scroll-auto` on
 *    `html` is set globally; rail-driven anchor jumps still smooth via
 *    `scroll-smooth` on the html element (in `globals.css`).
 * 5. **Notch / home indicator** — `env(safe-area-inset-*)` on the
 *    theme toggle + footer padding keeps controls off curved edges.
 * 6. **Hydration** — Shell reads `localStorage` for appearance with
 *    `suppressHydrationWarning` to avoid the flash mismatch.
 * 7. **Explore vs share** — `/explore/[token]` delegates to the same
 *    `SharePage`; one layout path covers both URLs.
 * 8. **Theme toggle alignment** — Toggle rides the header's right
 *    flex column; no absolute positioning needed.
 * 9. **Overscroll chaining** — `overscroll-y-contain` lifted from the
 *    inner band; iOS body scroll handles bounce naturally and the
 *    page no longer has nested scrollers to chain into anyway.
 * 10. **Inner scroll on `hide_scroll_rail` pages** — When the operator
 *     hides the rail (migration 354), the renderer still mounts in a
 *     bare wrapper using `VIRTUALSPACE_PUBLIC_SCROLL_CLASS` for its
 *     padding tokens; the wrapper is no longer a separate scroller.
 */

/**
 * Outer wrapper: at least the dynamic viewport tall so short pages
 * don't leave a gap above the footer; lets the body scroll naturally
 * for taller pages so iOS status-bar tap-to-top works.
 */
export const VIRTUALSPACE_PUBLIC_THEME_SHELL_CLASS =
  "min-h-dvh w-full flex flex-col text-foreground";

/**
 * Direct child of the shell — fills the available width, grows with
 * its content, and lets the body govern scroll. Background comes from
 * the shell (`vs-canvas-bg` dark / `bg-background` light) — keep main
 * transparent so the grid motif is visible on dark public pages.
 */
export const VIRTUALSPACE_PUBLIC_MAIN_CLASS =
  "w-full flex flex-col flex-1";

/**
 * Content column between header and footer. Pre-2026-05-07 this was
 * the inner scroll container itself; now it's just a max-width-aware
 * padding wrapper that participates in body scroll. Tokens preserved
 * (padding + space-y) so per-block spacing stays identical to the
 * shipped design.
 */
export const VIRTUALSPACE_PUBLIC_SCROLL_CLASS =
  "mx-auto w-full max-w-none py-6 sm:py-10 space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-16";

/**
 * Width tier for **reading content** (headings, rich text, file lists, link
 * buttons, contact cards, calendar / restaurant booking, scan list, survey
 * link). Caps comfortable line length while filling viewport width on phones.
 */
export const PUBLIC_VIRTUAL_SPACE_TEXT_TIER_CLASS =
  "mx-auto w-full max-w-[min(880px,100%)]";

/**
 * Width tier for **media content** (hero aerial / hero image, Matterport,
 * NavVis, point cloud, BIM/CAD, floor plan PDF, map/street/3D tiles, drone
 * video, external embed, location card). Default ceiling — the
 * `vs-theater-frame` global class in `globals.css` is the single source of
 * truth for the actual size + theater grow behavior; this token is exposed
 * for tests and any future caller that wants to align spacing without
 * pulling the frame component.
 */
export const PUBLIC_VIRTUAL_SPACE_MEDIA_TIER_CLASS =
  "mx-auto w-full max-w-[min(1280px,100%)]";
